import {
  ArrowLeftOutlined,
  CopyOutlined,
  EditOutlined,
} from "@ant-design/icons";
import {
  Button,
  Radio,
  Typography,
  Tooltip,
  Modal,
  DatePicker,
  Space,
} from "antd";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { JobDescriptionEntity } from "../../../Entities/JobDescription";
import useAccess from "../../../Hooks/useAccess";
import useOrganization from "../../../Hooks/useOrganization";
import { openNotification } from "../../../Components/Notification";
import jobDescriptionService from "../../../Service/job.description.service";
import AlertBanner from "../../../Components/Container/AlertBanner";
import dayjs from "dayjs";
import {
  checkSubscriptionStatus,
  showSubscriptionNotification,
} from "../../../Utils/subscriptionUtils";
import SubscriptionWarning from "../../../Components/SubscriptionWarning";

interface JobPostDetailsHeaderProps {
  jobPost: JobDescriptionEntity | null;
  handleTabChange: (tab: string) => void;
  currentTab: string;
  toggleJobStatus: (status: boolean) => Promise<any>;
  fetchJobPost: (id: string) => Promise<void>;
  jobPostId: string;
}

const JobPostDetailsHeader: React.FC<JobPostDetailsHeaderProps> = ({
  jobPost,
  handleTabChange,
  currentTab,
  toggleJobStatus,
  fetchJobPost,
  jobPostId,
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [endDateModalVisible, setEndDateModalVisible] =
    useState<boolean>(false);
  const [newEndDate, setNewEndDate] = useState<dayjs.Dayjs | null>(null);
  const [extending, setExtending] = useState(false);
  const { currentLimits, currentSubscription, changeSubscriptionMeasurements } =
    useAccess();
  const { org } = useOrganization();

  // Get theme color for the radio button
  const themeColor = org?.theme?.primaryColor || "#1890ff";

  // Helper functions for expiry detection
  const hasValidEndDate = (record: JobDescriptionEntity | null) =>
    !!record?.endDate && dayjs(record.endDate).isValid();

  const isPastEndDate = (record: JobDescriptionEntity | null) => {
    if (!hasValidEndDate(record)) return false;
    return dayjs(record!.endDate).endOf("day").isBefore(dayjs().startOf("day"));
  };

  const isInactiveButPastEndDate = (record: JobDescriptionEntity | null) =>
    !record?.isActive && isPastEndDate(record);

  const handleBack = () => {
    navigate("/job-posts");
  };

  const handleCloneJobPost = async () => {
    if (!jobPost?._id) return;

    const subscriptionCheck = await checkSubscriptionStatus();
    if (!subscriptionCheck.canAddPost) {
      showSubscriptionNotification(
        subscriptionCheck.message || "Upgrade Required"
      );
      setOpen(true);
      return;
    }
    navigate(`/add-job-post?id=${jobPost?._id}&mode=clone`);
  };

  const handleEditJobPost = () => {
    if (!jobPost?._id) return;
    navigate(`/add-job-post?id=${jobPost?._id}&mode=edit`);
  };

  const handletoggleJobStatus = async (status: boolean) => {
    const isPlanExpired = currentSubscription?.status === "inactive";
    if (isPlanExpired) {
      openNotification(
        "error",
        "Plan Expired",
        "Please renew or upgrade your plan to continue using the application."
      );
      return;
    }

    if (
      currentLimits?.jobPosts !== -1 &&
      currentLimits?.jobPosts &&
      currentSubscription &&
      currentSubscription?.measurements.jobPosts >= currentLimits?.jobPosts &&
      status
    ) {
      setOpen(true);
      return;
    }
    const isActive = await toggleJobStatus(status);
    if (currentTab === "uploads" && !isActive) {
      handleTabChange("details");
    }
    return;
  };

  const openExtendEndDateModal = () => {
    if (!jobPost) return;
    setNewEndDate(dayjs().add(30, "days"));
    setEndDateModalVisible(true);
  };

  const handleExtendEndDate = async () => {
    if (!jobPost || !newEndDate) {
      openNotification("error", "Error", "Please select a valid end date");
      return;
    }

    const wasAlreadyActive = jobPost.isActive;

    // Check subscription status before extending and reactivating
    if (!wasAlreadyActive) {
      // Check if plan is expired
      const isPlanExpired = currentSubscription?.status === "inactive";
      if (isPlanExpired) {
        openNotification(
          "error",
          "Plan Expired",
          "Please renew or upgrade your plan to continue using the application."
        );
        setEndDateModalVisible(false);
        setNewEndDate(null);
        return;
      }

      // Check if limit is reached (only when reactivating)
      if (
        currentLimits?.jobPosts &&
        currentLimits?.jobPosts !== -1 &&
        currentSubscription &&
        currentSubscription?.measurements.jobPosts >= currentLimits?.jobPosts
      ) {
        setEndDateModalVisible(false);
        setNewEndDate(null);
        setOpen(true);
        return;
      }
    }

    setExtending(true);
    try {
      const updatedJob = {
        ...jobPost,
        endDate: newEndDate.format("YYYY-MM-DD"),
        isActive: true,
      };

      await jobDescriptionService.updateJobDescriptionV2(
        updatedJob as any,
        jobPost._id
      );

      if (!wasAlreadyActive) {
        await jobDescriptionService.updateJobDescriptionStatus(
          jobPost._id,
          "publish"
        );
        changeSubscriptionMeasurements(1, "jobPosts");
      }

      // Refresh the job post data
      await fetchJobPost(jobPostId);

      setEndDateModalVisible(false);
      setNewEndDate(null);

      openNotification(
        "success",
        wasAlreadyActive ? "End Date Updated" : "Job Post Reactivated",
        wasAlreadyActive
          ? `End date has been extended to ${newEndDate.format("DD-MM-YYYY")}.`
          : `Job post has been reactivated with new end date: ${newEndDate.format(
              "DD-MM-YYYY"
            )}`
      );
    } catch (error: any) {
      openNotification(
        "error",
        "Error",
        error?.response?.data?.message ||
          (wasAlreadyActive
            ? "Failed to update end date"
            : "Failed to reactivate job post")
      );
    } finally {
      setExtending(false);
    }
  };

  const handleCancelEndDateModal = () => {
    setEndDateModalVisible(false);
    setNewEndDate(null);
  };

  return (
    <div className="job-post-details-page-title">
      <div className="job-post-details-page-title-row">
        <div className="job-post-details-page-title-section">
          <Button
            onClick={handleBack}
            size="middle"
            type="text"
            icon={<ArrowLeftOutlined style={{ fontSize: "20px" }} />}
          />

          <Typography.Title level={4} style={{ margin: 0 }} ellipsis>
            {jobPost?.title || "Job Post Details"}
          </Typography.Title>
        </div>

        <div className="job-post-details-page-controls-section">
          <Tooltip title="Edit this job post to modify its details, requirements, interview rounds, and other settings.">
            <Button
              type="default"
              size="middle"
              icon={<EditOutlined />}
              onClick={handleEditJobPost}
            >
              Edit
            </Button>
          </Tooltip>
          <Tooltip title="Create a duplicate copy of this job post with all the same details. You can then modify the cloned post as needed.">
            <Button
              type="default"
              size="middle"
              icon={<CopyOutlined />}
              onClick={handleCloneJobPost}
            >
              Clone
            </Button>
          </Tooltip>
          <Tooltip
            title={
              jobPost?.isActive
                ? "This job post is currently active and visible to candidates. Click Inactive to hide it."
                : "This job post is currently inactive and not visible to candidates. Click Active to enable it."
            }
          >
            <Radio.Group
              value={jobPost?.isActive ? "active" : "inactive"}
              onChange={(e) =>
                handletoggleJobStatus(e.target.value === "active")
              }
              optionType="button"
              buttonStyle="solid"
              className="job-post-details__status-radio"
              style={
                {
                  // Apply theme color to the selected button
                  "--theme-color": themeColor,
                } as React.CSSProperties
              }
            >
              <Radio.Button value="active">Active</Radio.Button>
              <Radio.Button value="inactive">Inactive</Radio.Button>
            </Radio.Group>
          </Tooltip>
        </div>
      </div>

      {isInactiveButPastEndDate(jobPost) && (
        <AlertBanner
          message="Job Post Expired"
          description={
            <span>
              This job post was automatically deactivated because its end date
              (
              {jobPost?.endDate
                ? dayjs(jobPost?.endDate).format("DD MMM YYYY")
                : "N/A"}
              ) has passed. You can extend the end date to reactivate it.
            </span>
          }
          type="warning"
          showIcon
          action={{
            label: "Extend now",
            onClick: openExtendEndDateModal,
          }}
        />
      )}

      {open && (
        <SubscriptionWarning
          open={open}
          handleClose={() => setOpen(false)}
          title="Limit reached"
          type="job-post-limit"
        />
      )}

      <Modal
        title="Extend Job Post End Date"
        open={endDateModalVisible}
        onOk={handleExtendEndDate}
        onCancel={handleCancelEndDateModal}
        okText={jobPost?.isActive ? "Extend" : "Extend & Activate"}
        cancelText="Cancel"
        confirmLoading={extending}
      >
        <div style={{ marginBottom: 16 }}>
          <p>
            The job post <strong>"{jobPost?.title}"</strong> has passed its end
            date (
            {jobPost?.endDate
              ? dayjs(jobPost.endDate).format("DD-MM-YYYY")
              : "N/A"}
            ).
          </p>
          <p>
            {jobPost?.isActive
              ? "Please select a new end date to keep this job post active."
              : "Please select a new end date to reactivate this job post."}
          </p>
        </div>
        <Space direction="vertical" style={{ width: "100%" }}>
          <DatePicker
            value={newEndDate}
            onChange={(date) => setNewEndDate(date)}
            placeholder="Select new end date"
            style={{ width: "100%" }}
            format="DD-MM-YYYY"
            disabledDate={(current) => {
              return current && current < dayjs().startOf("day");
            }}
          />
          {newEndDate && (
            <p style={{ color: "#666", fontSize: "12px", marginTop: 8 }}>
              Job post will close on: {newEndDate.format("DD-MM-YYYY")}
            </p>
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default JobPostDetailsHeader;
